<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useReportTimezone } from 'dashboard/composables/useReportTimezone';
import { resolveRangeInZone, todayInZone } from 'dashboard/helper/reportPeriod';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import format from 'date-fns/format';
import campaignReportsAPI from 'dashboard/api/campaignReports';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import CampaignRecipientsDialog from './components/CampaignRecipientsDialog.vue';
import RevenueTrackingSettings from '../inbox/RevenueTrackingSettings.vue';
import { useShellPeriod } from './composables/useShellPeriod';

// Campanhas de todos os canais da conta, fora da configuracao da caixa.
//
// Duas regras mandam nesta tela, e as duas vieram de numero que mentia:
//
// 1. Canal que nao informa entrega nem leitura nao mostra 0, mostra que o dado
//    nao existe. Zero dizia "ninguem recebeu" quando a verdade e "ninguem mede".
// 2. Campanha cuja lista nunca foi gravada nao ganha taxa de envio nenhuma, nem
//    0% nem 100%. Em producao havia uma campanha enviada inteira aparecendo como
//    0% enviada so porque o disparo dela nao registrava o tamanho da lista.
//
// A caixa deixou de ser o escopo da tela e virou filtro opcional: campanha de
// SMS e de canal por API nao aparecia em relatorio nenhum.

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { reportTimezone } = useReportTimezone();
const { isAdmin } = useAdmin();

const inboxes = useMapGetter('inboxes/getInboxes');

const isLoading = ref(false);
const hasError = ref(false);
const report = ref(null);
const rangeType = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const drilldown = ref(null);

const EMPTY = '·';

// Dentro da casa dos relatorios o periodo vem do seletor unico da casca, que
// ja entrega os dias no fuso da conta (e aceita periodo personalizado, que o
// select desta tela nao tinha). Fora da casa fica o select proprio.
const shellPeriod = useShellPeriod();

// Periodo fechado no fuso da CONTA: escolher "30 dias" numa maquina em Tokyo e
// noutra em Lisboa tem que pedir o mesmo intervalo.
const period = computed(() => {
  if (shellPeriod) {
    return {
      from: shellPeriod.period.value.startIso,
      to: shellPeriod.period.value.endIso,
    };
  }
  const zone = reportTimezone.value;
  const resolved = resolveRangeInZone(rangeType.value, zone) || {
    start: todayInZone(zone),
    end: todayInZone(zone),
  };
  return {
    from: format(resolved.start, 'yyyy-MM-dd'),
    to: format(resolved.end, 'yyyy-MM-dd'),
  };
});

const rangeOptions = [
  DATE_RANGE_TYPES.LAST_7_DAYS,
  DATE_RANGE_TYPES.LAST_30_DAYS,
  DATE_RANGE_TYPES.LAST_3_MONTHS,
];

const selectedInboxId = computed(() => {
  const fromUrl = Number(route.query.inbox);
  return (inboxes.value || []).some(inbox => inbox.id === fromUrl)
    ? fromUrl
    : null;
});

const campaigns = computed(() => report.value?.campaigns || []);
const totals = computed(() => report.value?.totals || null);
const writeFailures = computed(() => totals.value?.status_write_failures || 0);
// Venda so vira numero quando a conta mede venda: desligado mostra "nao medido".
const salesTracked = computed(() =>
  Boolean(report.value?.revenue?.tracking_enabled)
);
const salesCurrency = computed(() => report.value?.revenue?.currency || 'BRL');
const salesSettingsOpen = ref(false);
const salesSettingsRef = ref(null);

// Troca rapida de periodo ou caixa dispara pedidos em paralelo: so a resposta
// do pedido mais recente entra na tela, senao a lenta do filtro antigo vencia.
let latestRequestId = 0;

const fetchReport = async () => {
  latestRequestId += 1;
  const requestId = latestRequestId;
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await campaignReportsAPI.get({
      from: period.value.from,
      to: period.value.to,
      inboxId: selectedInboxId.value,
    });
    if (requestId !== latestRequestId) return;
    report.value = data;
  } catch (error) {
    if (requestId !== latestRequestId) return;
    // Erro nunca vira zero: a tela diz que nao conseguiu carregar e oferece
    // tentar de novo. Mostrar 0 aqui seria inventar um resultado.
    hasError.value = true;
    report.value = null;
  } finally {
    if (requestId === latestRequestId) isLoading.value = false;
  }
};

const onSelectInbox = event => {
  const value = event.target.value;
  const query = { ...route.query };
  if (value) query.inbox = value;
  else delete query.inbox;
  router.replace({ name: route.name, params: route.params, query });
};

const formatNumber = value =>
  value === null || value === undefined
    ? EMPTY
    : new Intl.NumberFormat('pt-BR').format(value);

const formatPercent = value =>
  value === null || value === undefined
    ? null
    : `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(value)}%`;

// A moeda vem de um campo livre das configuracoes. Codigo invalido faz o Intl
// lancar RangeError e derrubaria a tabela inteira, entao cai para BRL.
const formatMoney = value => {
  const amount = value || 0;
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: salesCurrency.value,
    }).format(amount);
  } catch (error) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  }
};

const salesCountLabel = count =>
  count === 1
    ? t('REPORT.CAMPAIGNS.SALES.COUNT_ONE')
    : t('REPORT.CAMPAIGNS.SALES.COUNT', { count: formatNumber(count) });

// Parametro tecnico da URL, igual em qualquer idioma: montado aqui para nao
// virar texto solto no template.
const utmParam = campaign => `utm_campaign=${campaign.utm_campaign}`;

// O utm_campaign e o que o cliente cola no link da pagina de venda. Sem ele a
// venda so cai na campanha pelo telefone ou e-mail do comprador.
const copyUtm = async campaign => {
  try {
    await copyTextToClipboard(campaign.utm_campaign);
    useAlert(t('REPORT.CAMPAIGNS.SALES.UTM_COPIED'));
  } catch (error) {
    useAlert(t('REPORT.CAMPAIGNS.SALES.UTM_COPY_ERROR'));
  }
};

// Reabre mesmo se a pessoa fechou o acordeão antes, e leva a tela até ele.
const openSalesSettings = () => {
  salesSettingsOpen.value = false;
  nextTick(() => {
    salesSettingsOpen.value = true;
    salesSettingsRef.value?.scrollIntoView({ behavior: 'smooth' });
  });
};

const channelLabel = campaign =>
  t(`REPORT.CAMPAIGNS.CHANNEL.${campaign.channel.key.toUpperCase()}`);

// Um numero so vira link quando existe lastro para abrir. Campanha antiga sem
// lastro mantem o numero historico, marcado, sem link: apagar o historico seria
// tao errado quanto fingir que da para conferi-lo.
const isClickable = (campaign, value) =>
  campaign.has_lastro && value !== null && value !== undefined && value > 0;

const openRecipients = (campaign, status, value) => {
  if (!isClickable(campaign, value)) return;
  drilldown.value = { campaign, status };
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchReport();
});

// Chave primitiva: outra troca na URL nao pode refazer a busca.
const periodKey = computed(() => `${period.value.from}|${period.value.to}`);
watch([periodKey, selectedInboxId], fetchReport);
</script>

<template>
  <ReportHeader :header-title="t('REPORT.CAMPAIGNS.TITLE')">
    <div class="flex items-center gap-2">
      <select
        v-if="!shellPeriod"
        v-model="rangeType"
        class="px-3 py-2 text-sm border rounded-xl border-n-strong bg-n-solid-2 text-n-slate-12"
        data-testid="campaign-reports-range"
        :aria-label="t('REPORT.CAMPAIGNS.FILTER.PERIOD')"
      >
        <option v-for="option in rangeOptions" :key="option" :value="option">
          {{ t(`REPORT.CAMPAIGNS.RANGE.${option.toUpperCase()}`) }}
        </option>
      </select>
      <select
        class="px-3 py-2 text-sm border rounded-xl border-n-strong bg-n-solid-2 text-n-slate-12"
        :value="selectedInboxId || ''"
        data-testid="campaign-reports-inbox"
        :aria-label="t('REPORT.CAMPAIGNS.FILTER.INBOX')"
        @change="onSelectInbox"
      >
        <option value="">{{ t('REPORT.CAMPAIGNS.ALL_INBOXES') }}</option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
    </div>
  </ReportHeader>

  <div
    v-if="isLoading"
    class="py-10 text-sm text-center text-n-slate-11"
    data-testid="campaign-reports-loading"
  >
    {{ t('REPORT.CAMPAIGNS.LOADING') }}
  </div>

  <div
    v-else-if="hasError"
    class="flex flex-col items-center gap-3 py-10"
    data-testid="campaign-reports-error"
  >
    <div class="flex items-center gap-2 text-sm text-n-slate-12">
      <span class="i-ri-error-warning-line size-4 text-n-ruby-9" />
      {{ t('REPORT.CAMPAIGNS.ERROR') }}
    </div>
    <button
      type="button"
      class="px-3 py-2 text-sm border rounded-xl border-n-strong text-n-slate-12 hover:bg-n-alpha-2"
      data-testid="campaign-reports-retry"
      @click="fetchReport"
    >
      {{ t('REPORT.CAMPAIGNS.RETRY') }}
    </button>
  </div>

  <div
    v-else-if="!campaigns.length"
    class="py-10 text-sm text-center text-n-slate-11"
    data-testid="campaign-reports-empty"
  >
    {{ t('REPORT.CAMPAIGNS.EMPTY') }}
  </div>

  <div v-else class="flex flex-col gap-4">
    <div
      v-if="writeFailures > 0"
      class="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl border-n-amber-6 text-n-slate-12"
      data-testid="campaign-reports-write-failures"
    >
      <span class="i-ri-alert-line size-4 text-n-amber-9" />
      {{ t('REPORT.CAMPAIGNS.WRITE_FAILURES', { count: writeFailures }) }}
    </div>

    <div
      v-if="!salesTracked && isAdmin"
      class="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm border rounded-xl border-n-weak text-n-slate-11"
      data-testid="campaign-reports-sales-off"
    >
      <span class="flex items-center gap-2">
        <span class="i-ri-information-line size-4" />
        {{ t('REPORT.CAMPAIGNS.SALES.NOT_MEASURED_HINT') }}
      </span>
      <Button
        :label="t('REPORT.CAMPAIGNS.SALES.OPEN_SETTINGS')"
        variant="faded"
        color="slate"
        size="sm"
        @click="openSalesSettings"
      />
    </div>

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <p class="text-xs text-n-slate-11">
          {{ t('REPORT.CAMPAIGNS.KPI.AUDIENCE') }}
        </p>
        <p class="text-2xl font-medium text-n-slate-12">
          {{ formatNumber(totals.audience) }}
        </p>
        <p v-if="totals.audience === null" class="text-xs text-n-slate-10">
          {{ t('REPORT.CAMPAIGNS.NO_AUDIENCE_RECORDED') }}
        </p>
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <p class="text-xs text-n-slate-11">
          {{ t('REPORT.CAMPAIGNS.KPI.SENT') }}
        </p>
        <p class="text-2xl font-medium text-n-slate-12">
          {{ formatNumber(totals.sent) }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{
            formatPercent(totals.sent_rate)
              ? t('REPORT.CAMPAIGNS.KPI.SENT_OF_LIST', {
                  percent: formatPercent(totals.sent_rate),
                })
              : t('REPORT.CAMPAIGNS.NO_AUDIENCE_RECORDED')
          }}
        </p>
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <p class="text-xs text-n-slate-11">
          {{ t('REPORT.CAMPAIGNS.KPI.DELIVERED') }}
        </p>
        <p class="text-2xl font-medium text-n-slate-12">
          {{ formatNumber(totals.delivered) }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{
            formatPercent(totals.delivered_rate)
              ? t('REPORT.CAMPAIGNS.KPI.DELIVERED_OF_SENT', {
                  percent: formatPercent(totals.delivered_rate),
                })
              : t('REPORT.CAMPAIGNS.NO_DELIVERY_CHANNEL')
          }}
        </p>
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <p class="text-xs text-n-slate-11">
          {{ t('REPORT.CAMPAIGNS.KPI.REPLIED') }}
        </p>
        <p class="text-2xl font-medium text-n-slate-12">
          {{ formatNumber(totals.replied) }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{
            formatPercent(totals.replied_rate)
              ? t(
                  totals.replied_base === 'delivered'
                    ? 'REPORT.CAMPAIGNS.KPI.REPLIED_OF_DELIVERED'
                    : 'REPORT.CAMPAIGNS.KPI.REPLIED_OF_SENT',
                  { percent: formatPercent(totals.replied_rate) }
                )
              : EMPTY
          }}
        </p>
      </div>
    </div>

    <div class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1">
      <table class="w-full text-sm" data-testid="campaign-reports-table">
        <thead>
          <tr class="text-xs text-left text-n-slate-11">
            <th class="px-4 py-3">{{ t('REPORT.CAMPAIGNS.COLUMN.NAME') }}</th>
            <th class="px-4 py-3">
              {{ t('REPORT.CAMPAIGNS.COLUMN.CHANNEL') }}
            </th>
            <th class="px-4 py-3 text-right">
              {{ t('REPORT.CAMPAIGNS.COLUMN.LIST') }}
            </th>
            <th class="px-4 py-3 text-right">
              {{ t('REPORT.CAMPAIGNS.COLUMN.SENT') }}
            </th>
            <th class="px-4 py-3 text-right">
              {{ t('REPORT.CAMPAIGNS.COLUMN.DELIVERED') }}
            </th>
            <th class="px-4 py-3 text-right">
              {{ t('REPORT.CAMPAIGNS.COLUMN.READ') }}
            </th>
            <th class="px-4 py-3 text-right">
              {{ t('REPORT.CAMPAIGNS.COLUMN.REPLIED') }}
            </th>
            <th class="px-4 py-3 text-right">
              {{ t('REPORT.CAMPAIGNS.COLUMN.SALES') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="campaign in campaigns"
            :key="campaign.id"
            class="border-t border-n-weak"
            :data-testid="`campaign-row-${campaign.id}`"
          >
            <td class="px-4 py-3">
              <span class="font-medium text-n-slate-12">
                {{ campaign.title }}
              </span>
              <!-- O utm aparece antes da primeira venda e com o rastreio
                   desligado: e ele que o cliente cola no link para comecar a medir. -->
              <div
                v-if="campaign.utm_campaign"
                class="flex items-center gap-1 mt-1 text-xs text-n-slate-11"
                :data-testid="`campaign-${campaign.id}-utm`"
              >
                <span>{{ t('REPORT.CAMPAIGNS.SALES.SALES_LINK') }}:</span>
                <code class="px-1 rounded bg-n-alpha-2 text-n-slate-12">
                  {{ utmParam(campaign) }}
                </code>
                <Button
                  variant="ghost"
                  color="slate"
                  size="xs"
                  icon="i-ri-file-copy-line"
                  :aria-label="
                    t('REPORT.CAMPAIGNS.SALES.COPY_UTM', {
                      title: campaign.title,
                    })
                  "
                  :title="
                    t('REPORT.CAMPAIGNS.SALES.COPY_UTM', {
                      title: campaign.title,
                    })
                  "
                  @click="copyUtm(campaign)"
                />
              </div>
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ channelLabel(campaign) }}
            </td>
            <td class="px-4 py-3 text-right">
              <span
                v-if="!campaign.audience.recorded"
                class="px-2 py-1 text-xs rounded-lg bg-n-alpha-2 text-n-slate-11"
                :data-testid="`campaign-${campaign.id}-list-unrecorded`"
              >
                {{ t('REPORT.CAMPAIGNS.NO_AUDIENCE_RECORDED') }}
              </span>
              <span v-else class="text-n-slate-12">
                {{ formatNumber(campaign.audience.size) }}
              </span>
            </td>
            <td class="px-4 py-3 text-right">
              <button
                v-if="isClickable(campaign, campaign.counts.sent)"
                type="button"
                class="underline text-n-blue-11 underline-offset-2"
                :data-testid="`campaign-${campaign.id}-sent-link`"
                @click="openRecipients(campaign, 'sent', campaign.counts.sent)"
              >
                {{ formatNumber(campaign.counts.sent) }}
              </button>
              <span
                v-else
                class="text-n-slate-11"
                :title="t('REPORT.CAMPAIGNS.NO_LASTRO')"
                :data-testid="`campaign-${campaign.id}-sent-plain`"
              >
                {{ formatNumber(campaign.counts.sent) }}
                <span
                  v-if="!campaign.has_lastro"
                  class="text-xs text-n-slate-10"
                >
                  {{ t('REPORT.CAMPAIGNS.NO_LASTRO') }}
                </span>
              </span>
            </td>
            <td
              v-if="!campaign.channel.delivery"
              class="px-4 py-3 text-xs text-right text-n-slate-10"
              colspan="3"
              :data-testid="`campaign-${campaign.id}-no-delivery`"
            >
              {{ t('REPORT.CAMPAIGNS.NO_DELIVERY_CHANNEL') }}
            </td>
            <template v-else>
              <td class="px-4 py-3 text-right">
                <button
                  v-if="isClickable(campaign, campaign.counts.delivered)"
                  type="button"
                  class="underline text-n-blue-11 underline-offset-2"
                  @click="
                    openRecipients(
                      campaign,
                      'delivered',
                      campaign.counts.delivered
                    )
                  "
                >
                  {{ formatNumber(campaign.counts.delivered) }}
                </button>
                <span v-else class="text-n-slate-11">
                  {{ formatNumber(campaign.counts.delivered) }}
                </span>
              </td>
              <td class="px-4 py-3 text-right">
                <button
                  v-if="isClickable(campaign, campaign.counts.read)"
                  type="button"
                  class="underline text-n-blue-11 underline-offset-2"
                  @click="
                    openRecipients(campaign, 'read', campaign.counts.read)
                  "
                >
                  {{ formatNumber(campaign.counts.read) }}
                </button>
                <span v-else class="text-n-slate-11">
                  {{ formatNumber(campaign.counts.read) }}
                </span>
              </td>
              <td class="px-4 py-3 text-right">
                <button
                  v-if="isClickable(campaign, campaign.counts.replied)"
                  type="button"
                  class="underline text-n-blue-11 underline-offset-2"
                  @click="
                    openRecipients(campaign, 'replied', campaign.counts.replied)
                  "
                >
                  {{ formatNumber(campaign.counts.replied) }}
                </button>
                <span v-else class="text-n-slate-11">
                  {{ formatNumber(campaign.counts.replied) }}
                </span>
              </td>
            </template>
            <td
              class="px-4 py-3 text-right"
              :data-testid="`campaign-${campaign.id}-sales`"
            >
              <span
                v-if="!salesTracked || !campaign.sales"
                class="text-xs text-n-slate-10"
              >
                {{ t('REPORT.CAMPAIGNS.SALES.NOT_MEASURED') }}
              </span>
              <span
                v-else-if="!campaign.sales.tracked"
                class="text-xs text-n-slate-10"
              >
                {{ t('REPORT.CAMPAIGNS.SALES.OPT_OUT') }}
              </span>
              <span v-else class="flex flex-col items-end">
                <span class="text-n-slate-12">
                  {{ formatMoney(campaign.sales.total) }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ salesCountLabel(campaign.sales.count) }}
                </span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="flex items-center gap-2 text-xs text-n-slate-10">
      <span class="i-ri-information-line size-4" />
      {{ t('REPORT.CAMPAIGNS.CLICK_HINT') }}
    </p>
  </div>

  <div
    v-if="isAdmin"
    ref="salesSettingsRef"
    class="mt-6"
    data-testid="campaign-reports-sales-settings"
  >
    <Accordion
      :title="t('REPORT.CAMPAIGNS.SALES_SETTINGS.SECTION')"
      :is-open="salesSettingsOpen"
    >
      <RevenueTrackingSettings @saved="fetchReport" />
    </Accordion>
  </div>

  <CampaignRecipientsDialog
    v-if="drilldown"
    :campaign="drilldown.campaign"
    :status="drilldown.status"
    @close="drilldown = null"
  />
</template>
